import {getLocalizationBasedOnLang} from '../../data/models/User/UserInfo';
import {Project} from '../../data/models/Projects/Project';

type ProjectCounts = Pick<
  Project,
  'coursesNumber' | 'microreelsNumber' | 'postsNumber'
>;

const totalItems = (counts: ProjectCounts) =>
  counts.coursesNumber + counts.microreelsNumber + counts.postsNumber;

const english = {
  title: 'Projects',
  newProject: 'New project',
  createProject: 'Create project',
  renameProject: 'Rename project',
  projectName: 'Name',
  projectNamePlaceholder: 'Give your project a name',
  save: 'Save',
  cancel: 'Cancel',
  open: 'Open',
  rename: 'Rename',
  edit: 'Edit',
  delete: 'Delete',
  coursesCount: (count: number) =>
    `${count} ${count === 1 ? 'course' : 'courses'}`,
  microreelsCount: (count: number) =>
    `${count} ${count === 1 ? 'microreel' : 'microreels'}`,
  postsCount: (count: number) => `${count} ${count === 1 ? 'post' : 'posts'}`,
  contentSummary: (counts: ProjectCounts) =>
    [
      english.coursesCount(counts.coursesNumber),
      english.microreelsCount(counts.microreelsNumber),
      english.postsCount(counts.postsNumber),
    ].join(', '),
  updatedAgo: (timeAgo: string) => `Updated ${timeAgo}`,
  itemsSummary: (counts: ProjectCounts) =>
    `${totalItems(counts)} ${
      totalItems(counts) === 1 ? 'item' : 'items'
    }: ${english.contentSummary(counts)}`,
  emptyTitle: 'No projects yet',
  emptySubtitle: 'Group your courses, microreels and posts into projects.',
  searchEmptyTitle: 'No projects match your search',
  searchEmptySubtitle: 'Try a different name.',
  deleteConfirm: (counts: ProjectCounts) =>
    totalItems(counts)
      ? `Delete this project? ${[
          counts.coursesNumber && english.coursesCount(counts.coursesNumber),
          counts.microreelsNumber &&
            english.microreelsCount(counts.microreelsNumber),
          counts.postsNumber && english.postsCount(counts.postsNumber),
        ]
          .filter(Boolean)
          .join(', ')} will stay in the workspace without a project.`
      : 'Delete this project?',
  projectCreated: 'Project created',
  projectUpdated: 'Project updated',
  projectDeleted: 'Project deleted',
  allProjects: 'All projects',
  courses: 'Courses',
  microreels: 'Microreels',
  posts: 'Posts',
  newCourse: 'New course',
  createMicroreels: 'Create microreels',
  newPost: 'New post',
  emptyCourses: 'No courses in this project yet',
  emptyMicroreels: 'No microreels in this project yet',
  emptyPosts: 'No posts in this project yet',
  emptyCoursesSubtitle: 'Create a new course or move an existing one here.',
  emptyMicroreelsSubtitle: 'Create new microreels or move existing ones here.',
  emptyPostsSubtitle: 'Create a new post or move an existing one here.',
  moveTitle: 'Move to project',
  project: 'Project',
  noProject: 'No project',
  newProjectInline: 'Or create a new project',
  move: 'Move',
  movedToProject: (name: string) => `Moved to ${name}`,
  removedFromProject: 'Removed from the project',
};

const welsh = {
  title: 'Prosiectau',
  newProject: 'Prosiect newydd',
  createProject: 'Crëwch brosiect',
  renameProject: 'Ailenwch y prosiect',
  projectName: 'Enw',
  projectNamePlaceholder: "Rhowch enw i'ch prosiect",
  save: 'Cadw',
  cancel: 'Canslo',
  open: 'Agor',
  rename: 'Ailenwi',
  edit: 'Golygu',
  delete: 'Dileu',
  coursesCount: (count: number) =>
    `${count} ${count === 1 ? 'cwrs' : 'cyrsiau'}`,
  microreelsCount: (count: number) =>
    `${count} ${count === 1 ? 'microreel' : 'microreels'}`,
  postsCount: (count: number) =>
    `${count} ${count === 1 ? 'post' : 'postiadau'}`,
  contentSummary: (counts: ProjectCounts) =>
    [
      welsh.coursesCount(counts.coursesNumber),
      welsh.microreelsCount(counts.microreelsNumber),
      welsh.postsCount(counts.postsNumber),
    ].join(', '),
  updatedAgo: (timeAgo: string) => `Diweddarwyd ${timeAgo}`,
  itemsSummary: (counts: ProjectCounts) =>
    `${totalItems(counts)} ${
      totalItems(counts) === 1 ? 'eitem' : 'eitemau'
    }: ${welsh.contentSummary(counts)}`,
  emptyTitle: 'Dim prosiectau eto',
  emptySubtitle:
    'Grwpiwch eich cyrsiau, microreels a’ch postiadau yn brosiectau.',
  searchEmptyTitle: 'Nid oes prosiectau’n cyfateb i’ch chwiliad',
  searchEmptySubtitle: 'Rhowch gynnig ar enw gwahanol.',
  deleteConfirm: (counts: ProjectCounts) =>
    totalItems(counts)
      ? `Dileu’r prosiect hwn? Bydd ${[
          counts.coursesNumber && welsh.coursesCount(counts.coursesNumber),
          counts.microreelsNumber &&
            welsh.microreelsCount(counts.microreelsNumber),
          counts.postsNumber && welsh.postsCount(counts.postsNumber),
        ]
          .filter(Boolean)
          .join(', ')} yn aros yn y gweithle heb brosiect.`
      : 'Dileu’r prosiect hwn?',
  projectCreated: 'Prosiect wedi’i greu',
  projectUpdated: 'Prosiect wedi’i ddiweddaru',
  projectDeleted: 'Prosiect wedi’i ddileu',
  allProjects: 'Pob prosiect',
  courses: 'Cyrsiau',
  microreels: 'Microreels',
  posts: 'Posts',
  newCourse: 'Cwrs newydd',
  createMicroreels: 'Crëwch microreels',
  newPost: 'Post newydd',
  emptyCourses: 'Dim cyrsiau yn y prosiect hwn eto',
  emptyMicroreels: 'Dim microreels yn y prosiect hwn eto',
  emptyPosts: 'Dim postiadau yn y prosiect hwn eto',
  emptyCoursesSubtitle:
    'Crëwch gwrs newydd neu symudwch gwrs sy’n bodoli eisoes yma.',
  emptyMicroreelsSubtitle:
    'Crëwch microreels newydd neu symudwch rai sy’n bodoli eisoes yma.',
  emptyPostsSubtitle:
    'Crëwch bost newydd neu symudwch bost sy’n bodoli eisoes yma.',
  moveTitle: 'Symudwch i brosiect',
  project: 'Prosiect',
  noProject: 'Dim prosiect',
  newProjectInline: 'Neu grëwch brosiect newydd',
  move: 'Symud',
  movedToProject: (name: string) => `Wedi’i symud i ${name}`,
  removedFromProject: 'Wedi’i dynnu o’r prosiect',
};

export const projectsText = getLocalizationBasedOnLang(english, welsh);
